import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FaArrowRight } from "react-icons/fa";
import { ModalShell } from "../../../atoms/ModalShell";
import { CustomButton } from "../../../atoms/CustomButton";
import { NoTranslate } from "../../../atoms/NoTranslate";
import { PartnerSummary } from "../../../../services/partnerAdmin";
import PartnerDemoRequestService, {
  DemoDecision,
  DemoRequest,
} from "../../../../services/partnerDemoRequests";
import { formatDay, partnerName } from "./partnerLabels";
import { grantAccessLink } from "../../../../utils/grantDuration";

const FIELD =
  "w-full rounded-md border border-gray-300 bg-white p-2.5 text-base text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 md:py-2 md:text-sm";

interface PartnerDemoRequestsProps {
  customerId: string;
  entityId: string;
  partners: PartnerSummary[];
  onOpenPartner: (partnerId: string) => void;
}

const errorMessage = (error: any) =>
  error?.response?.data?.message || error?.message || "Request failed";

/**
 * Demos distributors asked for their clients. Root grants the access with the private plan tool of Monitoring, then
 * marks the request here; the mark itself grants nothing.
 */
const PartnerDemoRequests: React.FC<PartnerDemoRequestsProps> = ({
  customerId,
  entityId,
  partners,
  onOpenPartner,
}) => {
  const [requests, setRequests] = useState<DemoRequest[]>([]);
  const [target, setTarget] = useState<{
    request: DemoRequest;
    decision: DemoDecision;
  } | null>(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setRequests(
        await PartnerDemoRequestService.listOpen(customerId, entityId)
      );
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  useEffect(() => {
    if (customerId && entityId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, entityId]);

  if (requests.length === 0) return null;

  const nameOf = (partnerId: string) => {
    const partner = partners.find((p) => p.partner_id === partnerId);
    return partner ? partnerName(partner) : partnerId;
  };

  const open = (request: DemoRequest, decision: DemoDecision) => {
    setTarget({ request, decision });
    setReason("");
  };

  const decide = async () => {
    if (!target) return;
    setSaving(true);
    try {
      await PartnerDemoRequestService.decide(
        customerId,
        entityId,
        target.request.id,
        { decision: target.decision, reason: reason.trim() }
      );
      toast.success(
        target.decision === "granted"
          ? "Request marked granted"
          : "Request marked refused"
      );
      setTarget(null);
      await load();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const granted = target?.decision === "granted";

  return (
    <section className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
      <p className="mb-3 text-sm font-semibold text-gray-900">
        Demo requests <span className="tabular-nums">({requests.length})</span>
      </p>
      <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
        {requests.map((request) => (
          <li
            key={request.id}
            className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-start"
          >
            <div className="min-w-0 flex-1 text-xs text-gray-600 sm:basis-64">
              <p className="text-sm font-medium text-gray-900">
                <NoTranslate>{request.entity_id}</NoTranslate>
              </p>
              {request.entity_name && (
                <p className="truncate text-gray-700">{request.entity_name}</p>
              )}
              <p>
                {`${request.days} day${
                  request.days > 1 ? "s" : ""
                }, asked on ${formatDay(request.created_at)} by `}
                <NoTranslate>{nameOf(request.partner_id)}</NoTranslate>
              </p>
              {request.message && (
                <p className="whitespace-pre-line break-words text-gray-700">
                  {request.message}
                </p>
              )}
              <Link
                to={grantAccessLink(request.entity_id, request.days)}
                className="group mt-1 inline-flex items-center gap-1 font-medium text-blue-700 hover:text-blue-800"
              >
                Grant access in Monitoring
                <FaArrowRight className="h-2.5 w-2.5 shrink-0" />
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <CustomButton
                variant="secondary"
                size="xs"
                title="Partner"
                onClick={() => onOpenPartner(request.partner_id)}
              />
              <CustomButton
                variant="secondary"
                size="xs"
                title="Refused"
                onClick={() => open(request, "refused")}
              />
              <CustomButton
                variant="primary"
                size="xs"
                title="Granted"
                onClick={() => open(request, "granted")}
              />
            </div>
          </li>
        ))}
      </ul>

      <ModalShell
        show={Boolean(target)}
        onClose={() => setTarget(null)}
        title={granted ? "Mark the demo granted" : "Refuse the demo"}
        subtitle={
          target
            ? `${target.request.entity_id}${
                target.request.entity_name
                  ? ` (${target.request.entity_name})`
                  : ""
              }`
            : undefined
        }
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <CustomButton
              variant="secondary"
              title="Cancel"
              onClick={() => setTarget(null)}
            />
            <CustomButton
              variant={granted ? "primary" : "danger"}
              title={granted ? "Mark granted" : "Mark refused"}
              disabled={reason.trim().length < 3 || saving}
              onClick={decide}
            />
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-gray-700">
            {granted
              ? "This only records the decision: grant the access first with Grant access in Monitoring."
              : "The request is closed. The distributor can send a new one."}
          </p>
          <textarea
            className={FIELD}
            rows={3}
            maxLength={500}
            placeholder="Reason (required, shown to the distributor)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </ModalShell>
    </section>
  );
};

export default PartnerDemoRequests;
